<template>
  <div class="watermark-container">
    <!-- 隐形水印层 -->
    <div class="invisible-watermark" ref="invisibleRef"></div>
    <!-- 可见水印层 -->
    <div class="visible-watermark" ref="visibleRef"></div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'InvisibleWatermark',
  setup() {
    const store = useStore()
    const invisibleRef = ref(null)
    const visibleRef = ref(null)
    
    // 获取用户名
    const username = computed(() => {
      const userinfo = store.getters['user/userinfo']
      return userinfo?.username || userinfo?.account || ''
    })

    // 生成水印文本（包含用户名和时间戳）
    const getWatermarkText = () => {
      const now = new Date()
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      return `${username.value} ${timestamp}`
    }

    // 创建隐形水印 Canvas（位置偏移，与可见水印错开）
    const createInvisibleWatermark = () => {
      if (!invisibleRef.value || !username.value) return

      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      canvas.width = 300
      canvas.height = 200
      
      // 隐形水印 - 极低透明度，位置在左上区域
      ctx.font = '18px Arial'
      ctx.fillStyle = 'rgba(0, 0, 0, 0.02)'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      
      ctx.translate(canvas.width / 4, canvas.height / 4)
      ctx.rotate(-20 * Math.PI / 180)
      
      const text = getWatermarkText()
      ctx.fillText(text, 0, 0)
      
      const dataUrl = canvas.toDataURL('image/png')
      invisibleRef.value.style.backgroundImage = `url(${dataUrl})`
    }

    // 创建可见水印 Canvas（位置偏移，与隐形水印错开）
    const createVisibleWatermark = () => {
      if (!visibleRef.value || !username.value) return

      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      canvas.width = 300
      canvas.height = 200
      
      // 可见水印 - 适中透明度，位置在右下区域
      ctx.font = '18px Arial'
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      
      ctx.translate(canvas.width * 3 / 4, canvas.height * 3 / 4)
      ctx.rotate(-20 * Math.PI / 180)
      
      const text = getWatermarkText()
      ctx.fillText(text, 0, 0)
      
      const dataUrl = canvas.toDataURL('image/png')
      visibleRef.value.style.backgroundImage = `url(${dataUrl})`
    }

    // 创建所有水印
    const createWatermark = () => {
      createInvisibleWatermark()
      createVisibleWatermark()
    }

    // 防止水印被删除 - MutationObserver
    let observer = null
    const setupObserver = () => {
      if (!invisibleRef.value || !visibleRef.value) return
      
      const parent = invisibleRef.value.parentNode
      if (!parent) return

      observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const removed = Array.from(mutation.removedNodes)
            // 如果隐形水印被删除，重新添加
            if (removed.includes(invisibleRef.value)) {
              parent.appendChild(invisibleRef.value)
            }
            // 如果可见水印被删除，重新添加
            if (removed.includes(visibleRef.value)) {
              parent.appendChild(visibleRef.value)
            }
          }
          // 如果水印样式被修改，重新设置
          if (mutation.type === 'attributes') {
            if (mutation.target === invisibleRef.value || mutation.target === visibleRef.value) {
              createWatermark()
            }
          }
        })
      })

      observer.observe(parent, {
        childList: true,
        attributes: true,
        subtree: true,
        attributeFilter: ['style', 'class']
      })
    }

    onMounted(() => {
      createWatermark()
      setupObserver()
    })

    onUnmounted(() => {
      if (observer) {
        observer.disconnect()
      }
    })

    // 监听用户名变化，重新生成水印
    watch(username, () => {
      createWatermark()
    })

    return {
      invisibleRef,
      visibleRef
    }
  }
}
</script>

<style scoped>
.watermark-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 99999;
}

.invisible-watermark,
.visible-watermark {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background-repeat: repeat;
}

.invisible-watermark {
  z-index: 1;
}

.visible-watermark {
  z-index: 2;
}
</style>
